import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEventValue, ClickEventValue } from 'google-map-react'
import GoogleMapReact from 'google-map-react'
import type { Cluster as ClusterRenderer } from '@googlemaps/markerclusterer'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'

import Cluster from '../Cluster'
import Marker from '../Marker'
import { Money } from '@s4l/stayforlong/kernel/Money'
import type { Currencies } from '@s4l/stayforlong/kernel/Currency'
import { useS4LContext } from '@s4l/react-context'
import { renderToStaticMarkup } from 'react-dom/server'
import MapLoader from '../Loader'
import styles from './index.module.css'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import debug from 'debug'

interface MarkerProps {
  id: number
  data: {
    amount: number
    currency: Currencies
  }
  center: google.maps.LatLngLiteral
}

interface TileProps {
  apiKey: string
  center: google.maps.LatLngLiteral
  zoom?: number
  bounds?: google.maps.LatLngBoundsLiteral
  children: React.ReactNode
  mapId: string
  markers: MarkerProps[]
  isLoading?: boolean
  useClustering?: boolean
  defaultMarker?: number
  onChange?: (params: {
    marginBounds: google.maps.LatLngBoundsLiteral
    center: google.maps.LatLngLiteral
    zoom: number
    hasClickedOnCluster: boolean
    zoomDirection: number
  }) => void
  onClick: (event: ClickEventValue) => void
  onMarkerClick: (marker: google.maps.marker.AdvancedMarkerElement, markerData: MarkerProps) => void
  onInit: (map: google.maps.Map) => void
  onClusterClick?: (cluster: {
    count: number
    markers: Array<google.maps.marker.AdvancedMarkerElement>
  }) => void
}

const log = debug('s4l:uikit:map')

const Tile = ({
  apiKey,
  center,
  zoom,
  children,
  mapId,
  markers,
  isLoading,
  useClustering,
  defaultMarker,
  bounds,
  onChange,
  onClick,
  onMarkerClick,
  onInit,
  onClusterClick,
}: TileProps) => {
  const { i18n } = useS4LContext()
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [isInit, setIsInit] = useState(false)

  const activeMarkerRef = useRef<number | null>(defaultMarker ?? null)

  const markersRef = useRef<Map<number, google.maps.marker.AdvancedMarkerElement>>(new Map())
  const boundsMarkerDebugRef = useRef<Map<number, google.maps.marker.AdvancedMarkerElement>>(
    new Map()
  )
  const clustererRef = useRef<MarkerClusterer | null>(null)

  const [hasClickedOnCluster, setHasClickedOnCluster] = useState(false)
  const [zoomInternal, setZoomInternal] = useState(zoom)
  const [isInitWithDefaultMarker, setIsInitWithDefaultMarker] = useState(false)

  const { data: market } = useUseCase('GetCurrentMarketSharedUseCase')

  const updateMarkerActiveState = useCallback(
    (markerId?: number) => {
      if (activeMarkerRef.current !== null) {
        const prevMarker = markersRef.current.get(activeMarkerRef.current)
        if (prevMarker?.content) {
          const prevElement = prevMarker.content as HTMLElement
          prevElement.classList.remove('active')
        }
      }

      if (!markerId) {
        activeMarkerRef.current = null
        return
      }

      const newMarker = markersRef.current.get(markerId)
      if (newMarker?.content) {
        newMarker.zIndex = 1001
        const newElement = newMarker.content as HTMLElement
        newElement.classList.add('active')
      }

      activeMarkerRef.current = markerId
    },
    [markersRef]
  )

  const onload = (m: google.maps.Map) => {
    setMap(m)
    onInit(m)
  }

  const handleMarkerClick = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement, markerData: MarkerProps) => {
      onMarkerClick(marker, markerData)
    },
    [onMarkerClick]
  )

  const handleClick = ({ event }: ClickEventValue) => {
    const target = event.target as HTMLElement
    if (target.tagName !== 'BUTTON') {
      onClick(event)
      updateMarkerActiveState()
    }
  }

  useEffect(() => {
    if (!map || isLoading || !market?.isFilled()) return

    clustererRef.current?.clearMarkers?.()

    markersRef.current.forEach(marker => {
      marker.map = null // eslint-disable-line no-param-reassign
    })

    markersRef.current.clear()

    const googleMarkers = markers.map(marker => {
      const markerHTML = renderToStaticMarkup(
        <Marker
          market={market.value}
          i18n={i18n}
          moneyJSON={Money.fromJSONOrUndefined({
            amount: marker.data.amount,
            currency: marker.data.currency,
          })?.toJSON()}
        />
      )

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = markerHTML
      const markerElement = tempDiv.firstElementChild // Get the actual Marker element

      const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
        position: marker.center,
        content: markerElement, // Now this is the Marker element itself, not a wrapper div
        map: useClustering && marker.id !== defaultMarker ? null : map,
      })

      advancedMarker.id = marker.id.toString()

      markersRef.current.set(marker.id, advancedMarker)

      advancedMarker.addEventListener('mouseenter', () => {
        googleMarkers.forEach(googleMarker => {
          googleMarker.zIndex = 0 // eslint-disable-line no-param-reassign

          if (googleMarker.id === activeMarkerRef.current?.toString()) {
            googleMarker.zIndex = 1001 // eslint-disable-line no-param-reassign
          }
        })

        advancedMarker.zIndex = 1000
      })

      advancedMarker.addEventListener('click', event => {
        event.stopPropagation()
        event.preventDefault()
        updateMarkerActiveState(marker.id)
        handleMarkerClick(advancedMarker, marker)
      })

      return advancedMarker
    })

    if (activeMarkerRef.current) {
      updateMarkerActiveState(activeMarkerRef.current)
    }

    if (!useClustering) return

    clustererRef.current = new MarkerClusterer({
      markers: googleMarkers.filter(marker => marker.id !== defaultMarker?.toString()),
      map,
      onClusterClick: (_, cluster) => {
        if (cluster.bounds) {
          setHasClickedOnCluster(true)
          map.fitBounds(cluster.bounds)

          onClusterClick?.(
            cluster as {
              count: number
              markers: Array<google.maps.marker.AdvancedMarkerElement>
            }
          )
        }
      },
      renderer: {
        render: ({ count, position, markers: markersRender }: ClusterRenderer) => {
          let currency: Currencies = 'EUR'

          const minValue = markersRender.reduce((acc, marker) => {
            const markerElement = marker as google.maps.marker.AdvancedMarkerElement
            const markerContent = markerElement.content as HTMLDivElement
            const amount = Number(markerContent.dataset.amount)
            currency = (markerContent.dataset.currency as Currencies) ?? 'EUR'
            return Math.min(acc, amount)
          }, Infinity)

          const markerHTML = renderToStaticMarkup(
            <Cluster
              count={count}
              i18n={i18n}
              moneyJSON={Money.fromJSONOrUndefined({
                amount: minValue,
                currency,
              })?.toJSON()}
              market={market.value}
            />
          )

          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = markerHTML
          const element = tempDiv.firstElementChild // Get the actual Marker element

          const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            content: element,
            zIndex: count,
          })

          const shadowRoot = element?.getRootNode()

          if (shadowRoot) {
            shadowRoot.addEventListener('mouseenter', e => {
              if (e.target instanceof HTMLElement) {
                e.target.style.zIndex = '1000'
              }
            })

            shadowRoot.addEventListener('mouseleave', e => {
              if (e.target instanceof HTMLElement) {
                e.target.style.zIndex = ''
              }
            })
          }

          return marker
        },
      },

      algorithm: new SuperClusterAlgorithm({
        minPoints: (map?.getZoom() ?? 0) > 10 ? 5 : 2,
        radius: (map?.getZoom() ?? 0) > 15 ? 80 : 160,
      }),
    })
  }, [map, markers, i18n, isLoading, market?.value]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map) return

    if (bounds) {
      if (!isInitWithDefaultMarker) {
        map.fitBounds(bounds)
      } else {
        setIsInitWithDefaultMarker(false)
      }

      if (log.enabled) {
        boundsMarkerDebugRef.current.forEach(marker => {
          marker.map = null // eslint-disable-line no-param-reassign
        })
        boundsMarkerDebugRef.current.clear()

        boundsMarkerDebugRef.current.set(
          1,
          new google.maps.marker.AdvancedMarkerElement({
            position: { lat: bounds.north, lng: bounds.east },
            map,
          })
        )

        // Southwest corner
        boundsMarkerDebugRef.current.set(
          2,
          new google.maps.marker.AdvancedMarkerElement({
            position: { lat: bounds.south, lng: bounds.west },
            map,
          })
        )

        // Northwest corner
        boundsMarkerDebugRef.current.set(
          3,
          new google.maps.marker.AdvancedMarkerElement({
            position: { lat: bounds.north, lng: bounds.west },
            map,
          })
        )

        // Southeast corner
        boundsMarkerDebugRef.current.set(
          4,
          new google.maps.marker.AdvancedMarkerElement({
            position: { lat: bounds.south, lng: bounds.east },
            map,
          })
        )
      }
    } else {
      map.setCenter?.(center)
      map.setZoom?.(zoom ?? 10)
    }
  }, [bounds?.east, bounds?.north, bounds?.south, bounds?.west, map, isInit]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsInitWithDefaultMarker(defaultMarker !== undefined)
    if (map && defaultMarker) {
      map.setCenter?.(center)
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateMarkerActiveState(defaultMarker)
  }, [defaultMarker, updateMarkerActiveState])

  function onChangeInternal(params: ChangeEventValue) {
    onChange?.({
      marginBounds: {
        east: params.marginBounds.ne.lng,
        north: params.marginBounds.ne.lat,
        south: params.marginBounds.sw.lat,
        west: params.marginBounds.sw.lng,
      },
      center: params.center,
      zoom: params.zoom,
      hasClickedOnCluster,
      zoomDirection: params.zoom - (zoomInternal ?? 10),
    })

    setZoomInternal(params.zoom)
    setHasClickedOnCluster(false)
  }

  return (
    <div className={styles.mapWrapper} data-testid="map">
      <GoogleMapReact
        bootstrapURLKeys={{ key: apiKey, libraries: ['marker'], language: i18n.locale }}
        defaultZoom={15}
        center={center}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map: m }) => onload(m)}
        options={{ mapId, clickableIcons: false, fullscreenControl: false }}
        onClick={handleClick}
        onTilesLoaded={() => setIsInit(true)}
        onChange={onChangeInternal}
      />
      {isLoading && <MapLoader />}
      {children}
    </div>
  )
}

Tile.displayName = 'MapTile'
export default Tile
